import { useEffect, useRef, useCallback } from "react";
import { Loader2, Sparkles } from "lucide-react";
import Markdown from "react-markdown";
import type { UIMessage } from "ai";
import { useHelpStore } from "@/lib/store/help.store";

interface AiChatMessagesProps {
  messages: UIMessage[];
  isStreaming: boolean;
}

const HELPCENTER_URL = "https://hilfe.dentdesk.ch";

export const AiChatMessages: React.FC<AiChatMessagesProps> = ({
  messages,
  isStreaming,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const openArticle = useHelpStore((state) => state.openArticle);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string | undefined) => {
      if (!href) return;
      if (href.startsWith(HELPCENTER_URL + "/")) {
        e.preventDefault();
        const slug = href.replace(HELPCENTER_URL + "/", "");
        if (slug && !slug.startsWith("changelog")) {
          openArticle(slug);
        } else {
          window.open(href, "_blank");
        }
      }
    },
    [openArticle]
  );

  return (
    <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
      {messages.map((message) => {
        const textPart = message.parts?.find(
          (p: { type: string }) => p.type === "text"
        );
        const text =
          textPart && "text" in textPart ? (textPart as { text: string }).text : "";

        if (message.role === "user") {
          return (
            <div key={message.id} className="flex justify-end">
              <div className="max-w-[85%] bg-gray-900 text-white rounded-2xl rounded-br-md px-3 py-2">
                <p className="text-xs leading-relaxed whitespace-pre-wrap">{text}</p>
              </div>
            </div>
          );
        }

        return (
          <div key={message.id} className="flex gap-2">
            <div className="shrink-0 w-6 h-6 rounded-full bg-gray-50 flex items-center justify-center mt-0.5">
              <Sparkles className="h-3 w-3 text-gray-400" />
            </div>
            <div className="max-w-[85%] text-xs text-gray-700 leading-relaxed">
              <Markdown
                components={{
                  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                  strong: ({ children }) => (
                    <strong className="font-semibold text-gray-800">{children}</strong>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc pl-4 mb-2 space-y-0.5">{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal pl-4 mb-2 space-y-0.5">{children}</ol>
                  ),
                  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      onClick={(e) => handleLinkClick(e, href)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {children}
                    </a>
                  ),
                  code: ({ children }) => (
                    <code className="text-[11px] bg-gray-100 rounded px-1 py-0.5 font-mono">
                      {children}
                    </code>
                  ),
                  h3: ({ children }) => (
                    <h3 className="font-semibold text-gray-800 mt-2 mb-1">
                      {children}
                    </h3>
                  ),
                  hr: () => <hr className="my-2 border-gray-100" />,
                }}
              >
                {text}
              </Markdown>
            </div>
          </div>
        );
      })}

      {isStreaming && messages[messages.length - 1]?.role === "user" && (
        <div className="flex gap-2">
          <div className="shrink-0 w-6 h-6 rounded-full bg-gray-50 flex items-center justify-center">
            <Sparkles className="h-3 w-3 text-gray-400" />
          </div>
          <Loader2 className="h-4 w-4 animate-spin text-gray-400 mt-1" />
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
};
